import React, { useState } from 'react';
import axios from 'axios';
import { X, Plus, Trash2 } from 'lucide-react';

const ACCENT = '#667eea';
const TEXT_TITLE = '#1a1a2e';
const TEXT_BODY = '#334155';
const TEXT_MUTED = '#64748b';
const BORDER = '#e2e8f0';
const SURFACE = '#ffffff';

const ROLE_LABELS = {
  title_column_id:    { label: 'Title column',    types: ['text'] },
  status_column_id:   { label: 'Status column',   types: ['dropdown'] },
  due_date_column_id: { label: 'Due date column', types: ['date'] },
  labels_column_id:   { label: 'Labels column',   types: ['multi_select'] }
};

function SheetPlanSettings({ sheet, onClose, onSheetUpdated, onColumnsChanged }) {
  const [savingRole, setSavingRole] = useState(null);
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');
  const [newColumnType, setNewColumnType] = useState('text');
  const [deletingPlan, setDeletingPlan] = useState(false);

  const compatible = (types) => (sheet.columns || []).filter((c) => types.includes(c.type) && !c.system_type);

  const setRole = async (role, columnId) => {
    setSavingRole(role);
    try {
      const res = await axios.put(`/api/sheets/${sheet.id}/roles`, { [role]: columnId === '' ? null : parseInt(columnId, 10) });
      onSheetUpdated(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update role');
    } finally {
      setSavingRole(null);
    }
  };

  const addColumn = async (e) => {
    e.preventDefault();
    if (!newColumnName.trim()) return;
    try {
      await axios.post(`/api/sheets/${sheet.id}/columns`, { name: newColumnName.trim(), type: newColumnType, config: (newColumnType === 'dropdown' || newColumnType === 'multi_select') ? { options: [] } : null });
      setNewColumnName('');
      setNewColumnType('text');
      setAddingColumn(false);
      onColumnsChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add column');
    }
  };

  const deleteColumn = async (col) => {
    if (!window.confirm(`Delete column "${col.name}"? Its data will be lost.`)) return;
    try {
      await axios.delete(`/api/sheets/columns/${col.id}`);
      onColumnsChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete column');
    }
  };

  const deletePlan = async () => {
    if (!window.confirm(`Delete the plan "${sheet.name}"? This cannot be undone.`)) return;
    setDeletingPlan(true);
    try {
      await axios.delete(`/api/sheets/${sheet.id}`);
      onClose({ deleted: true });
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete plan');
      setDeletingPlan(false);
    }
  };

  const userColumns = (sheet.columns || []).filter((c) => !c.system_type);

  return (
    <aside style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: '420px',
      maxWidth: '100vw',
      height: '100vh',
      background: SURFACE,
      borderLeft: `1px solid ${BORDER}`,
      boxShadow: '-10px 0 30px rgba(0,0,0,0.06)',
      zIndex: 50,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <header style={{ padding: '20px 24px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 600, fontStyle: 'italic', color: TEXT_TITLE }}>Plan settings</h2>
        <button type="button" onClick={() => onClose()} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: TEXT_MUTED, padding: 4, display: 'flex' }}>
          <X size={18} />
        </button>
      </header>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Card fields */}
        <section>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Card fields</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {Object.entries(ROLE_LABELS).map(([role, meta]) => {
              const options = compatible(meta.types);
              const value = sheet[role] || '';
              return (
                <label key={role} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ fontSize: '0.85rem', color: TEXT_BODY, fontWeight: 500 }}>{meta.label}</span>
                  <select
                    value={value}
                    onChange={(e) => setRole(role, e.target.value)}
                    disabled={savingRole === role}
                    style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${BORDER}`, fontSize: '0.9rem', background: SURFACE, color: TEXT_BODY, cursor: 'pointer' }}
                  >
                    <option value="">— None —</option>
                    {options.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  {options.length === 0 && (
                    <span style={{ fontSize: '0.75rem', color: TEXT_MUTED }}>No compatible columns. Add one below.</span>
                  )}
                </label>
              );
            })}
          </div>
        </section>

        {/* Custom columns */}
        <section>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 600, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Custom columns</span>
            <button type="button" onClick={() => setAddingColumn((v) => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'transparent', border: 'none', color: ACCENT, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
              <Plus size={14} /> Add column
            </button>
          </div>

          {addingColumn && (
            <form onSubmit={addColumn} style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: '8px', marginBottom: '10px' }}>
              <input
                type="text"
                value={newColumnName}
                onChange={(e) => setNewColumnName(e.target.value)}
                placeholder="Column name"
                style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${BORDER}`, fontSize: '0.9rem' }}
              />
              <select value={newColumnType} onChange={(e) => setNewColumnType(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: `1px solid ${BORDER}`, fontSize: '0.9rem' }}>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
                <option value="dropdown">Dropdown</option>
                <option value="multi_select">Multi-select</option>
                <option value="checkbox">Checkbox</option>
                <option value="contact">Contact</option>
              </select>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setAddingColumn(false)} style={{ padding: '6px 14px', background: 'transparent', color: TEXT_MUTED, border: `1px solid ${BORDER}`, borderRadius: '999px', fontSize: '0.85rem', cursor: 'pointer' }}>Cancel</button>
                <button type="submit" style={{ padding: '6px 14px', background: ACCENT, color: 'white', border: 'none', borderRadius: '999px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>Add</button>
              </div>
            </form>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {userColumns.length === 0 && (
              <span style={{ fontSize: '0.85rem', color: TEXT_MUTED }}>No custom columns yet.</span>
            )}
            {userColumns.map((c) => (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: '#f8fafc', border: `1px solid ${BORDER}`, borderRadius: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.9rem', color: TEXT_BODY, fontWeight: 500 }}>{c.name}</span>
                  <span style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>{c.type}</span>
                </div>
                <button type="button" onClick={() => deleteColumn(c)} title="Delete column" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px', display: 'flex' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Danger zone */}
        <section>
          <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Danger zone</div>
          <button type="button" onClick={deletePlan} disabled={deletingPlan} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '999px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
            <Trash2 size={14} /> Delete plan
          </button>
        </section>
      </div>
    </aside>
  );
}

export default SheetPlanSettings;
